// ─────────────────────────────────────────────────────────────────────────────
// MonthlyReview — Phase 8 Pass E
//
// Rebuilt from a static 4-column table into a detector-delta narrative.
// For each of the past 6 months, shows:
//   • How the combined estimated cost changed month-over-month
//   • Which detectors improved (cost went down), worsened (cost went up),
//     resolved (cost dropped to 0), or newly emerged
//   • A brief qualitative sentence per month
//
// Data source: detector_snapshots table. We take the LAST snapshot of each
// month for each detector's "estimated_cost" metric, then diff adjacent months.
//
// Below the narrative, the original P&L table is kept as supporting context.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useState } from "react";
import { Card } from "../../components/ui/Card";
import { SectionTitle } from "../../components/ui/SectionTitle";
import { Pill } from "../../components/ui/Pill";
import { C } from "../../lib/colors";
import { fmt$ } from "../../lib/format";
import { SUPABASE_URL, authFetch } from "../../lib/supabase";

// Human-readable detector names
const DETECTOR_NAMES = {
  revenge:           "Revenge Trading",
  size_tilt:         "Position Size Tilt",
  win_streak_euphoria: "Win-Streak Euphoria",
  hour_cliff:        "Hour Cliff",
  day_of_week:       "Day-of-Week Pattern",
  time_in_trade:     "Time-in-Trade Tilt",
  overtrading:       "Overtrading",
  size_adherence:    "Size Adherence",
  drawdown_proximity:"Drawdown Proximity",
  stop_adherence:    "Stop Adherence",
  stop_widening:     "Stop Widening",
  r_multiples:       "R-Multiples",
  rule_violations:   "Rule Violations",
  // Context detectors
  news_event:        "News Event Trading",
  manual_exit:       "Manual Exit Cliff",
  market_regime:     "Market Regime Edge",
  setup_edge:        "Setup Edge",
  // Psychology detectors
  fear_spike:        "Fear-Spike Trading",
  impulsiveness:     "Impulsiveness Cost",
  low_focus:         "Low Focus Cliff",
  mental_state:      "Mental State Edge",
  conviction_mismatch: "Conviction Calibration",
};

// ─── Data fetching ────────────────────────────────────────────────────────────
async function loadMonthlySnapshots({ userId, accountId }) {
  // Fetch last 6 months of estimated_cost snapshots across all detectors.
  // We only care about "estimated_cost" metric — that's the headline $ number
  // that drives the narrative.
  const since = new Date();
  since.setMonth(since.getMonth() - 6);
  const sinceStr = since.toISOString().slice(0, 10);

  const acctFilter = accountId
    ? `account_id=eq.${encodeURIComponent(accountId)}`
    : "account_id=is.null";

  const params = [
    `user_id=eq.${encodeURIComponent(userId)}`,
    acctFilter,
    `metric_name=eq.estimated_cost`,
    `snapshot_date=gte.${sinceStr}`,
    `select=detector_id,snapshot_date,metric_value,confidence`,
    `order=snapshot_date.asc`,
    `limit=5000`,
  ].join("&");

  const res = await authFetch(`${SUPABASE_URL}/rest/v1/detector_snapshots?${params}`);
  if (!res.ok) throw new Error(`Monthly snapshots ${res.status}`);
  return res.json();
}

// Group rows by YYYY-MM, then per detector take the LAST row of that month.
// Returns: Map<"YYYY-MM", Map<detectorId, { cost, confidence }>>
function groupByMonth(rows) {
  // First pass: collect all rows per (month, detector)
  const monthly = new Map();
  for (const r of rows) {
    const month = String(r.snapshot_date).slice(0, 7); // "YYYY-MM"
    if (!monthly.has(month)) monthly.set(month, new Map());
    const byDet = monthly.get(month);
    // Keep the last (latest date) row per detector
    if (!byDet.has(r.detector_id) || r.snapshot_date > byDet.get(r.detector_id).date) {
      byDet.set(r.detector_id, {
        cost:       Number(r.metric_value) || 0,
        confidence: r.confidence,
        date:       r.snapshot_date,
      });
    }
  }
  return monthly;
}

// Format month label: "2026-04" → "April 2026"
function fmtMonth(ym) {
  const [y, m] = ym.split("-");
  const date = new Date(Number(y), Number(m) - 1, 1);
  return date.toLocaleString("en-US", { month: "long", year: "numeric" });
}

// ─── Month card ────────────────────────────────────────────────────────────────
function MonthCard({ month, current, previous, isLatest }) {
  const currentTotal  = Array.from(current.values()).reduce((s, d) => s + d.cost, 0);
  const previousTotal = previous
    ? Array.from(previous.values()).reduce((s, d) => s + d.cost, 0)
    : null;

  const delta = previousTotal != null ? currentTotal - previousTotal : null;
  const improved = delta != null && delta < -5;
  const worsened = delta != null && delta > 5;

  // Detector-level deltas
  const allDetectors = new Set([
    ...Array.from(current.keys()),
    ...(previous ? Array.from(previous.keys()) : []),
  ]);

  const changes = [];
  for (const id of allDetectors) {
    const cur  = current.get(id);
    const prev = previous?.get(id);
    const curCost  = cur?.cost ?? 0;
    const prevCost = prev?.cost ?? 0;
    const diff = curCost - prevCost;

    if (!prev && cur && curCost > 0) {
      changes.push({ id, type: "new",      diff: curCost,  label: DETECTOR_NAMES[id] || id });
    } else if (prev && !cur) {
      changes.push({ id, type: "resolved", diff: -prevCost, label: DETECTOR_NAMES[id] || id });
    } else if (Math.abs(diff) > 5) {
      changes.push({ id, type: diff > 0 ? "worse" : "better", diff, label: DETECTOR_NAMES[id] || id });
    }
  }

  // Sort: resolutions first (good news), then biggest cost changes
  changes.sort((a, b) => {
    const order = { resolved: 0, better: 1, worse: 2, new: 3 };
    return (order[a.type] ?? 4) - (order[b.type] ?? 4);
  });

  const [expanded, setExpanded] = useState(isLatest);

  const borderColor = improved ? C.green : worsened ? C.red : C.border;
  const costColor   = currentTotal > 0 ? C.red : C.green;

  return (
    <div style={{
      border: `1px solid ${borderColor}40`,
      borderLeft: `3px solid ${borderColor}`,
      borderRadius: 10,
      overflow: "hidden",
      marginBottom: 8,
    }}>
      {/* Month header */}
      <div
        onClick={() => setExpanded(e => !e)}
        style={{
          padding: "14px 18px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer",
          background: expanded ? "rgba(255,255,255,0.02)" : "transparent",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
            {fmtMonth(month)}
          </span>
          {isLatest && (
            <span style={{ fontSize: 10, color: C.blue, background: C.blue + "22", padding: "2px 8px", borderRadius: 10, fontWeight: 700 }}>
              Current
            </span>
          )}
          {delta != null && (
            <span style={{
              fontSize: 12, fontWeight: 700,
              color: improved ? C.green : worsened ? C.red : C.muted,
            }}>
              {improved ? "↓ improved" : worsened ? "↑ worsened" : "→ stable"}
              {" "}
              {Math.abs(delta) > 1 && `${fmt$(Math.abs(delta))}/mo`}
            </span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 10, color: C.muted }}>Est. cost</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: costColor, fontFamily: "monospace" }}>
              {currentTotal > 0 ? fmt$(-currentTotal) : "—"}/mo
            </div>
          </div>
          <span style={{ color: C.muted, fontSize: 12 }}>{expanded ? "▼" : "▶"}</span>
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div style={{ padding: "0 18px 16px", borderTop: `1px solid ${C.border}20` }}>
          {/* Change summary */}
          {changes.length > 0 ? (
            <div style={{ paddingTop: 12 }}>
              <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
                {previous ? "vs previous month" : "first month of data"}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {changes.map(ch => (
                  <div key={ch.id} style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "8px 12px",
                    background: ch.type === "resolved" ? C.green + "0f"
                                : ch.type === "better"   ? C.green + "0a"
                                : ch.type === "worse"    ? C.red + "0a"
                                : C.blue + "0a",
                    borderRadius: 6,
                    border: `1px solid ${
                      ch.type === "resolved" || ch.type === "better" ? C.green + "30"
                      : ch.type === "worse" ? C.red + "30"
                      : C.blue + "30"
                    }`,
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 14 }}>
                        {ch.type === "resolved" ? "✓" : ch.type === "better" ? "↓" : ch.type === "worse" ? "↑" : "★"}
                      </span>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{ch.label}</div>
                        <div style={{ fontSize: 11, color: C.muted }}>
                          {ch.type === "resolved" ? "Pattern resolved — no longer detected"
                           : ch.type === "new"     ? "Newly detected this month"
                           : ch.type === "better"  ? `Cost reduced by ${fmt$(Math.abs(ch.diff))}/mo`
                           :                          `Cost increased by ${fmt$(Math.abs(ch.diff))}/mo`}
                        </div>
                      </div>
                    </div>
                    <span style={{
                      fontSize: 12, fontWeight: 700,
                      color: ch.type === "resolved" || ch.type === "better" ? C.green : ch.type === "worse" ? C.red : C.blue,
                      fontFamily: "monospace",
                    }}>
                      {ch.type === "resolved" ? fmt$(0) + "/mo"
                       : ch.type === "new"    ? fmt$(-ch.diff) + "/mo"
                       : `${ch.diff > 0 ? "+" : ""}${fmt$(ch.diff)}/mo`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ paddingTop: 12, fontSize: 12, color: C.muted, fontStyle: "italic" }}>
              {previous ? "No significant changes vs previous month." : "Baseline — first month of detector data."}
            </div>
          )}

          {/* All active detectors this month */}
          {current.size > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
                Active patterns this month
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {Array.from(current.entries())
                  .filter(([, d]) => d.cost > 0)
                  .sort((a, b) => b[1].cost - a[1].cost)
                  .map(([id, d]) => (
                    <div key={id} style={{
                      padding: "7px 12px",
                      background: "#0b0d19",
                      borderRadius: 6,
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      flexShrink: 0,
                    }}>
                      <span style={{ fontSize: 11, color: C.sub }}>
                        {DETECTOR_NAMES[id] || id}
                      </span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: C.red, fontFamily: "monospace" }}>
                        {fmt$(-d.cost)}/mo
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── P&L reference table (kept from original) ────────────────────────────────
function PnlTable({ monthlyData }) {
  if (!monthlyData?.length) return null;
  return (
    <Card style={{ marginTop: 16 }}>
      <SectionTitle>Monthly P&L Reference</SectionTitle>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${C.border}` }}>
              {["Month", "Trades", "Win Rate", "P&L"].map(h => (
                <th key={h} style={{ padding: "10px", textAlign: h === "Month" ? "left" : "center", color: C.muted, fontWeight: 500 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {monthlyData.map((m, i) => (
              <tr key={i} style={{ borderBottom: `1px solid ${C.border}20` }}>
                <td style={{ padding: "10px", fontWeight: 600, color: C.text }}>{m.month}</td>
                <td style={{ padding: "10px", textAlign: "center", color: C.sub }}>{m.trades}</td>
                <td style={{ padding: "10px", textAlign: "center", color: m.winRate == null ? C.muted : m.winRate >= 60 ? C.green : C.yellow }}>
                  {m.winRate != null ? `${m.winRate}%` : "—"}
                </td>
                <td style={{ padding: "10px", textAlign: "center" }}>
                  <Pill value={m.pnl} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MonthlyReview — main export
// ─────────────────────────────────────────────────────────────────────────────
export function MonthlyReview({ userId, accountId, monthlyData = [] }) {
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    if (!userId || !accountId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);

    loadMonthlySnapshots({ userId, accountId })
      .then(data => {
        if (!cancelled) { setRows(data); setLoading(false); }
      })
      .catch(err => {
        if (!cancelled) { setError(err.message); setLoading(false); }
      });

    return () => { cancelled = true; };
  }, [userId, accountId]);

  // Group rows into monthly Map<YYYY-MM, Map<detectorId, {cost,confidence}>>
  const monthlySnapshots = useMemo(() => groupByMonth(rows), [rows]);

  // Sorted months ascending
  const months = useMemo(
    () => Array.from(monthlySnapshots.keys()).sort(),
    [monthlySnapshots]
  );

  const hasData = months.length > 0;

  const latestMonth = months[months.length - 1];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ marginBottom: 8 }}>
        <SectionTitle>Monthly Review</SectionTitle>
        <p style={{ fontSize: 12, color: C.muted, margin: "4px 0 0" }}>
          Month-over-month changes in your behavioral patterns.
          Based on detector snapshots — patterns that improved, worsened, or emerged.
        </p>
      </div>

      {loading && (
        <Card style={{ padding: 32, textAlign: "center" }}>
          <div style={{ color: C.muted, fontSize: 13 }}>Loading snapshot history…</div>
        </Card>
      )}

      {error && (
        <Card style={{ padding: 24 }}>
          <div style={{ color: C.red, fontSize: 13 }}>⚠ Could not load snapshot history: {error}</div>
        </Card>
      )}

      {!loading && !error && !hasData && (
        <Card style={{ padding: 32 }}>
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <div style={{ fontSize: 28, marginBottom: 10 }}>📆</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 8 }}>
              Building your history
            </div>
            <div style={{ fontSize: 13, color: C.muted, maxWidth: 440, margin: "0 auto", lineHeight: 1.7 }}>
              Monthly Review tracks how your behavioral patterns change over time.
              Snapshots are written automatically as you use the app — come back
              after a few weeks to see your first month-over-month comparison.
            </div>
          </div>
          {/* What gets tracked */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 10,
            marginTop: 8,
          }}>
            {[
              { icon: "📉", label: "Cost per pattern", desc: "Est. monthly $ drain per detector" },
              { icon: "✅", label: "Resolved patterns", desc: "Patterns that dropped to zero" },
              { icon: "⚠️", label: "Emerging patterns", desc: "New detectors that fired this month" },
              { icon: "📈", label: "Month-over-month delta", desc: "Improved, stable, or worsened" },
            ].map(item => (
              <div key={item.label} style={{
                padding: "12px 14px",
                background: "#0b0d19",
                border: `1px solid ${C.border}`,
                borderRadius: 8,
              }}>
                <div style={{ fontSize: 18, marginBottom: 6 }}>{item.icon}</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 3 }}>{item.label}</div>
                <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>{item.desc}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {!loading && !error && hasData && (
        <>
          {/* Month cards in reverse order (latest first) */}
          {[...months].reverse().map((month, i) => {
            const idx = months.indexOf(month);
            const prevMonth = idx > 0 ? months[idx - 1] : null;
            return (
              <MonthCard
                key={month}
                month={month}
                current={monthlySnapshots.get(month)}
                previous={prevMonth ? monthlySnapshots.get(prevMonth) : null}
                isLatest={month === latestMonth}
              />
            );
          })}
        </>
      )}

      {/* P&L reference table always shown if data exists */}
      <PnlTable monthlyData={monthlyData} />
    </div>
  );
}
